import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  ArrowBack,
  PlayArrow,
  CloudUpload
} from '@mui/icons-material';
import TimerWidget from './TimerWidget';
import BookmarkButton from './BookmarkButton';
import UserAvatar from '../../../../components/Avatar/UserAvatar';

/**
 * 题目头部组件
 * 包含题目信息、操作按钮和工具按钮
 */
function QuestionHeader({
  question,
  user,
  executing = false,
  submitting = false,
  onExecute,
  onSubmit,
  onTimerChange,
  hideTitle = false,
  rightActions = null,
  isTeacher = false,
  onDifficultyChange = null
}) {
  const navigate = useNavigate();

  if (!question) {
    return null;
  }

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return '简单';
      case 'medium':
        return '中等';
      case 'hard':
        return '困难';
      case 'exam':
        return '考试原题';
      default:
        return difficulty;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      case 'exam':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <AppBar
      position="static"
      color="default"
      elevation={0}
      sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
    >
      <Toolbar
        sx={{
          px: { xs: 1, sm: 2 },
          py: 1.5,
          gap: 2,
          justifyContent: 'space-between',
          minHeight: '64px !important'
        }}
      >
        {/* Left Side - Navigation and Question Info */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            flex: { xs: '1 1 auto', md: '0 0 auto' },
            minWidth: 0,
            maxWidth: { xs: 'calc(50% - 80px)', md: 'none' }
          }}
        >
          <IconButton
            edge="start"
            size="small"
            onClick={() => navigate(-1)}
            sx={{
              flexShrink: 0,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <ArrowBack />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            {!hideTitle && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'text.primary',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {question.title || 'Loading...'}
              </Typography>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              {isTeacher && onDifficultyChange ? (
                <FormControl size="small" sx={{ minWidth: 72, display: { xs: 'none', sm: 'block' } }}>
                  <Select
                    value={question.difficulty || 'easy'}
                    onChange={(e) => onDifficultyChange(e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      '& .MuiSelect-select': { py: 0.25, px: 1 }
                    }}
                  >
                    <MenuItem value="easy">简单</MenuItem>
                    <MenuItem value="medium">中等</MenuItem>
                    <MenuItem value="hard">困难</MenuItem>
                    <MenuItem value="exam">考试原题</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={getDifficultyLabel(question.difficulty)}
                  color={getDifficultyColor(question.difficulty)}
                  size="small"
                  sx={{
                    height: '24px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    display: { xs: 'none', sm: 'flex' }
                  }}
                />
              )}
              <Chip
                label={question.category}
                color="primary"
                size="small"
                variant="outlined"
                sx={{
                  height: '24px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  display: { xs: 'none', md: 'flex' }
                }}
              />
            </Stack>
          </Stack>
        </Stack>

        {/* Center: Action Buttons */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            position: { xs: 'static', md: 'absolute' },
            left: { md: '50%' },
            transform: { md: 'translateX(-50%)' },
            order: { xs: 3, md: 2 },
            mt: { xs: 1, md: 0 },
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'center', md: 'flex-start' }
          }}
        >
          <Button
            variant="contained"
            size="medium"
            startIcon={<PlayArrow />}
            onClick={onExecute}
            disabled={executing}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: executing ? 'none' : 2,
              '&:hover': {
                boxShadow: executing ? 'none' : 4,
                transform: executing ? 'none' : 'translateY(-1px)',
                transition: 'all 0.2s'
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled'
              }
            }}
          >
            {executing ? 'Running...' : 'Run'}
          </Button>
          <Button
            variant="contained"
            size="medium"
            startIcon={<CloudUpload />}
            onClick={onSubmit}
            disabled={submitting}
            color="success"
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: submitting ? 'none' : 2,
              '&:hover': {
                boxShadow: submitting ? 'none' : 4,
                transform: submitting ? 'none' : 'translateY(-1px)',
                transition: 'all 0.2s'
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled'
              }
            }}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </Stack>

        {/* Right Side: Utilities */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            flex: { xs: '0 0 auto', md: '0 0 auto' },
            order: { xs: 2, md: 3 }
          }}
        >
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

          <TimerWidget onTimerChange={onTimerChange} />

          <BookmarkButton questionId={question.id} user={user} size="small" />

          {rightActions}

          {user && (
            <Tooltip title={user.name || user.email || 'User'}>
              <span>
                <UserAvatar
                  user={user}
                  size={32}
                  sx={{
                    flexShrink: 0,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s'
                    }
                  }}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export default QuestionHeader;

